/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {isDevelopmentEnvironment} from '@ion/core/src/environment/environment';
import {info, warn} from '@ion/core/src/logger/logger';
import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
import {
  capitalizeFirstLetter,
  contains,
  normalize
} from '@ion/core/src/utils/StringUtil';
import {
  applyTranslations as applyTranslationFiles
} from '@ion/dls/src/theme/resellers/Resellers';

/**
 * Defines the Resellers for which the BMW Web Portal Application can be deployed.
 * The Resellers listed define the customer resellers for which the application
 * provides full white-labelling support.
 */
export enum Reseller {
  bmw = 'bmw',
  rrmc = 'rrmc'
}

/**
 * Provides the pre-defined `environment.json` configuration entry keys.
 */
export interface AppConfig {
  appKey?: string,
  rrmcAppKey?: string,
  appTitle?: string,
  apiVersion?: string,
  serviceURL?: string,
  websocketTokenURL?: string,
  websocketURL?: string,
  arcGISJSAPIVersion?: string,
  arcGISHost?: string,
  arcGISProxyURL?: string,
  arcGISServiceURLPrefix?: string,
  arcGISServiceAPIPrefix?: string,
  arcGISPortalURL?: string,
  mapServiceURL?: string,
  androidAppPackage?: string,
  androidIdURL?: string,
  iOSAppPackage?: string,
  iOSIdURL?: string,
  webMapDashletURL?: string,
  websocketReconnectInterval?: number,
};

/**
 * Defines the reseller domain names which are included in the host for the reseller
 * in all environments.
 */
export const Domains = {
  [Reseller.bmw]  : 'bmwsecurity',
  [Reseller.rrmc] : 'royceguardian'
};

/**
 * Provides a convenience function for determining if the application is running as
 * a Web Portal Reseller; i.e. deployed to a reseller environment, such as RRMC.
 *
 * ```typescript
 *   import {isReseller} from '@/security/Resellers';
 *   ...
 *
 *   if (isReseller()) {
 *     // execute something only when running / deployed to a reseller (e.g. RRMC, etc.) ...
 *   }
 * ```
 *
 * @returns `true` if the application is running / deployed to a reseller environment,
 *          such as RRMC.
 */
export const isReseller = (): boolean => (
  getAppReseller() !== Reseller.bmw
);

/**
 * Provides a convenience function for determining if the application is running
 * / deployed to the Rolls Royce (RRMC) environment.
 *
 * ```typescript
 *   import {isRRMC} from '@/security/Resellers';
 *   ...
 *
 *   if (isRRMC()) {
 *     // execute something only when running / deployed to Rolls Royce environment ...
 *   }
 * ```
 *
 * @returns `true` if the application is running / deployed to a reseller environment,
 *          such as RRMC.
 */
export const isRRMC = (): boolean => (
  getAppReseller() === Reseller.rrmc
);

/**
 * Provides a convenience function for determining if the application is running as
 * the default deployment reseller (i.e. 'BMW').
 *
 * ```typescript
 *   import {isBMW} from '@/security/Resellers';
 *   ...
 *
 *   if (isBMW()) {
 *     // execute something only when running / deployed to BMW ...
 *   }
 * ```
 *
 * @returns `true` if the application is running / deployed to the BMW Web Portal
 */
export const isBMW = (): boolean => (
  getAppReseller() === Reseller.bmw
);

/**
 * Provides a convenience API which determines the reseller of the application and
 * returns the result.
 *
 * The reseller is determined based on the following rules:
 *
 *  1. If the app is running locally, the `?reseller` query parameter is inspected
 *     in order to determine the reseller for which the application is running.
 *  2. If the application is deployed and running in pre-prod, or production, the
 *     host URL is inspected to determine the reseller for which the application is
 *     running.
 *  3. If a reseller can not be resolved, this function falls back to the default
 *     reseller, 'bmw' (`Reseller.bmw`).
 *
 * @returns The Reseller for which the application is deployed, defaults to 'bmw'
 *          if a reseller can not be resolved.
 */
export const getAppReseller = (): Reseller => {
  if (isDevelopmentEnvironment()) {
    const app = getParameter('reseller');
    if (!app) {
      const message = `
        Unable to Resolve a Web Portal Reseller. If running the Web Portal for a Reseller, you must provide the
        '?reseller' Query String Parameter. For example: http://127.0.0.1:5800/login?reseller=rrmc.
        If you are running the application for the default BMW Web Portal, you can ignore this Warning.
      `;
      warn(normalize(message));
    }
    return Reseller[(app || Reseller.bmw).toLowerCase()];
  } else {
    const host = globalThis.location.host;
    if (contains(host, Domains[Reseller.rrmc])) {
      return Reseller.rrmc;
    }
  }
  return Reseller.bmw;
};

/**
 * Provides a utility which returns the reseller translation file name based on
 * the domain to which the application has been deployed.
 *
 * ```typescript
 *   import {initializeLanguageAsync} from '@ion/core/src/locale/locale';
 *   import {getTranslationsFilename} from '@/security/Resellers';
 *   ...
 *   // qa-rolls-royceguardian.lojack.eu
 *   await initializeLanguageAsync(getTranslationsFilename()); // e.g. reseller-rrmc.json
 * ```
 *
 * @returns the `reseller-{name}.json` file which is to be loaded, if any.
 */
export const getTranslationsFilename = (): string => {
  const host = globalThis.location.host;
  if (contains(host, Domains[Reseller.rrmc])) {
    return `reseller-${Reseller.rrmc}`;
  }
};

/**
 * Provides a utility which appends the reseller specific translation file
 * overrides (when deployed to a BMW Web Portal Reseller) to the list of
 * translation files. The reseller override file is determined based on
 * the domain to which the application has been deployed.
 *
 * Reseller translation files in in the form of: `reseller-{name}.json`,
 * for example, if deployed to the Rolls Royce Environment, the domain
 * will be used to map to the RRMC reseller translation file, which is
 * `reseller-rrmc.json`.
 *
 * For example:
 *
 * ```typescript
 *   // e.g. login.tsx ...
 *   import {applyTranslations} from '@/security/Resellers';
 *   ...
 *
 *   const translations = withTranslation('login', 'common']);
 *   console.log(translations);
 *   // 'login','common','reseller-rrmc.json'
 *
 *   const LoginPage = withTranslation(
 *     translations
 *   )(withPageErrorBoundary(Page)) as PageLayoutConsumer;
 * ```
 *
 * @param translations
 */
export const applyTranslations = (translations: string[]): string[] => (
  applyTranslationFiles(
    translations,
    'reseller',
    getTranslationsFilename
  )
);

/**
 * The `applyConfigOverrides` API determines the deployment context in which the
 * application is running and updates the *environment.json* default entries (such
 * as, `appKey`, etc.) to the values of the given reseller overrides of the same
 * name  (if any), otherwise, the default  entry values are retained.
 *
 * This effectively allows for overriding `environment.json` entries with reseller
 * specific value.
 *
 * ```typescript
 *  // _app.tsx
 *  import applyConfigOverrides from '@/security/Resellers';
 *  ...
 *
 *  loadConfig(config => {
 *    ...
 *    // resets the default configuration entry values; such as 'appKey', etc.
 *    // to the reseller prefixed entry's of the same name, such as 'rrmcAppKey'
 *    applyConfigOverrides(config);
 *  });
 *
 * ```
 *
 * @param   config The application configuration as provided via `environment.json`.
 *          which provides the reseller specific override keys. Reseller override keys
 *          must be defined as follows: {reseller-id}{Entry}}. For example, "rrmcAppKey",
 *          entry would result in the "appKey" entry being set to the value of the
 *          "rrmcAppKey", etc.
 * @returns The application configuration with the default entries updated to the
 *          values of the reseller overrides (if any).
 */
export const applyConfigOverrides = (config: AppConfig): AppConfig => {
  const reseller = getAppReseller();
  if (reseller && reseller !== Reseller.bmw) {
    Object.entries(config).forEach(([entry]) => {
      const key = `${reseller}${capitalizeFirstLetter(entry)}`;
      const override = config[key];
      if (!isUndefined(override)) {
        config[entry] = override;
        info(`Resolved Web Portal Override (${key}), resetting default ${entry} to: ${override}`);
      }
    });
  }
  return config;
};

export default applyConfigOverrides;
